"use client";
import * as React from "react";
import { atom, useAtom } from "jotai";
import { authClient } from "@acme/auth/client";
import { generateUUID } from "@acme/core/lib/utils";
import { Pitch, PitchFormDraft, ToastMessage } from "@acme/entities";
import {
  UsePitchDetails,
  UsePitchDetailsParams,
  UsePitchDetailsReturn,
} from "~/modules/main/hooks/use-pitch-details/use-pitch-details.interface";
import { trpcReactQueryApi } from "~/trpc/react";

// -------------------------------------------
// Global atoms for shared state across views
// -------------------------------------------

// Core pitch context/state
const pitchAtom = atom<Pitch | null>(null);

// Team meta resolved client-side via auth utilities
const teamIdAtom = atom<string | null>(null);
const teamNameAtom = atom<string | null>(null);

// Ownership/meta derived for footer display
const ownerNameAtom = atom<string | null>(null);
// We don't have last-editor in schema; keep null for MVP
const lastEditorNameAtom = atom<string | null>(null);

// Timestamps for info strip
const createdAtAtom = atom<Date | null>(null);
const updatedAtAtom = atom<Date | null>(null);

// Loading & generation states
const isLoadingAtom = atom<boolean>(false);
const isGeneratingAtom = atom<boolean>(false);
const generationProgressAtom = atom<number>(0);
const errorMessageAtom = atom<string | null>(null);

// Current inputs draft for (re)generation
const formDraftAtom = atom<PitchFormDraft>({
  productTitle: "",
  productDescription: "",
  targetCustomer: null,
  customerPainPoint: null,
  additionalContext: null,
});

// Store the last attempted payload to support retryGenerate
const lastAttemptedFormDraftAtom = atom<PitchFormDraft | null>(null);

// Local toast queue
const toastsAtom = atom<ToastMessage[]>([]);

// The current pitch id being viewed/loaded
const currentPitchIdAtom = atom<string | null>(null);

// -------------------------------------------
// Helper: Friendly error mapping
// -------------------------------------------

/**
 * Maps raw TRPC/Neon error messages into user-friendly text focused on team context.
 * Includes structured error mapping for persistence failures to enable client retry UX.
 */
function mapFriendlyError(message?: string | null): string {
  const msg = message ?? "";
  if (msg.includes("PITCH_SAVE_FAILED")) {
    return "We couldn't save the generated pitch. Please try again.";
  }
  if (msg.includes("No active team found")) return "No active team found. Please select a team and try again.";
  if (msg.includes("Invalid team context")) return "You are not authorized for this team.";
  if (msg.includes("Pitch not found")) return "Pitch not found.";
  return msg || "Something went wrong. Please try again.";
}

// -------------------------------------------
// Hook Implementation
// -------------------------------------------

/**
 * Pitch Details domain hook.
 * - Uses TRPC for all server interactions.
 * - RLS is enforced on the server (scoped to active team/organization).
 * - Active team context resolved via @acme/auth client utilities.
 */
export function usePitchDetails(params: UsePitchDetailsParams): UsePitchDetailsReturn {
  // Global/shared state
  const [pitch, setPitch] = useAtom(pitchAtom);
  const [teamId, setTeamId] = useAtom(teamIdAtom);
  const [teamName, setTeamName] = useAtom(teamNameAtom);
  const [ownerName, setOwnerName] = useAtom(ownerNameAtom);
  const [lastEditorName] = useAtom(lastEditorNameAtom);
  const [createdAt, setCreatedAt] = useAtom(createdAtAtom);
  const [updatedAt, setUpdatedAt] = useAtom(updatedAtAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [isGenerating, setIsGenerating] = useAtom(isGeneratingAtom);
  const [generationProgress, setGenerationProgress] = useAtom(generationProgressAtom);
  const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);
  const [formDraft, setFormDraft] = useAtom(formDraftAtom);
  const [lastAttemptedFormDraft, setLastAttemptedFormDraft] = useAtom(lastAttemptedFormDraftAtom);
  const [toasts, setToasts] = useAtom(toastsAtom);
  const [currentPitchId, setCurrentPitchId] = useAtom(currentPitchIdAtom);

  // Derived: Active organization/team from @acme/auth client
  // Use organization only for display name; DO NOT derive teamId from organization id.
  const { data: activeOrg } = authClient.useActiveOrganization();

  // Resolve the active team id from the active member (correct source of teamId)
  React.useEffect(() => {
    let isMounted = true;
    async function resolveActiveTeamId() {
      try {
        const member = await authClient.organization.getActiveMember();
        const resolvedTeamId = member?.data?.teamId ?? null;
        if (isMounted) {
          setTeamId((prev) => (prev !== resolvedTeamId ? resolvedTeamId : prev));
        }
      } catch {
        if (isMounted) setTeamId(null);
      }
    }
    void resolveActiveTeamId();
    return () => {
      isMounted = false;
    };
  }, [setTeamId]);

  // Build a local userId -> displayName map from org members when org changes
  const [membersNameMap, setMembersNameMap] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    let isMounted = true;
    async function loadOrgMembers() {
      try {
        // Set teamName from active organization for UI purposes
        setTeamName(activeOrg?.name ?? null);
        if (!activeOrg?.id) {
          if (isMounted) setMembersNameMap({});
          return;
        }
        // Fetch full organization details to derive member names
        const org = (await authClient.organization.getFullOrganization()).data;
        const map: Record<string, string> = {};
        org?.members?.forEach((m) => {
          const name = m.user.name ?? m.user.email ?? "Unknown";
          map[m.userId] = name;
        });
        if (isMounted) setMembersNameMap(map);
      } catch {
        if (isMounted) setMembersNameMap({});
      }
    }
    void loadOrgMembers();
    return () => {
      isMounted = false;
    };
  }, [activeOrg, setTeamName]);

  // -------------------------------------------
  // TRPC React Query - Queries & Mutations
  // -------------------------------------------

  // TRPC cache utils for invalidations
  const utils = trpcReactQueryApi.useUtils();

  // Pitch fetch by id (enabled only when both pitch id and teamId are available)
  const {
    data: fetchedPitch,
    isLoading: isPitchLoading,
    isError: isPitchError,
  } = trpcReactQueryApi.main.pitches.byId.useQuery(
    // pass undefined when disabled to avoid param mismatch; casting keeps TS happy while "enabled" gates execution
    teamId && currentPitchId ? { id: currentPitchId, teamId } : (undefined as unknown as { id: string; teamId: string }),
    { enabled: !!currentPitchId && !!teamId },
  );

  // Mutations for title/body/delete
  const updateTitleMutation = trpcReactQueryApi.main.pitches.updateTitle.useMutation({
    onSuccess: async (updated) => {
      setPitch(updated);
      setUpdatedAt(updated.updatedAt);
      // Keep local draft's productTitle in sync with title edits
      setFormDraft((prev) => ({ ...prev, productTitle: updated.title }));
      await utils.main.pitches.byId.invalidate();
      await utils.main.pitches.list.invalidate();
    },
    onError: (error) => {
      const friendly = mapFriendlyError(error?.message);
      setErrorMessage(friendly);
      enqueueToast(friendly, "error");
    },
  });

  const updateBodyMutation = trpcReactQueryApi.main.pitches.updateBody.useMutation({
    onSuccess: async (updated) => {
      setPitch(updated);
      setUpdatedAt(updated.updatedAt);
      await utils.main.pitches.byId.invalidate();
      await utils.main.pitches.list.invalidate();
      enqueueToast("Pitch saved", "success");
    },
    onError: (error) => {
      const friendly = mapFriendlyError(error?.message);
      setErrorMessage(friendly);
      enqueueToast(friendly, "error");
    },
  });

  const softDeleteMutation = trpcReactQueryApi.main.pitches.softDelete.useMutation({
    onSuccess: async () => {
      await utils.main.pitches.byId.invalidate();
      await utils.main.pitches.list.invalidate();
      enqueueToast("Pitch moved to trash", "success");
      setPitch(null);
    },
    onError: (error) => {
      const friendly = mapFriendlyError(error?.message);
      setErrorMessage(friendly);
      enqueueToast(friendly, "error");
    },
  });

  // Generation mutation (callbacks will be handled manually in command for better retry control)
  const generateFromFormMutation = trpcReactQueryApi.main.pitches.generateFromForm.useMutation();

  // Keep loading indicator in sync with the query lifecycle
  React.useEffect(() => {
    if (!currentPitchId) return;
    setIsLoading(isPitchLoading);
    if (isPitchError) {
      setErrorMessage("Failed to load pitch");
    }
  }, [currentPitchId, isPitchLoading, isPitchError, setIsLoading, setErrorMessage]);

  // When pitch changes from fetch, hydrate local atoms and owner meta
  React.useEffect(() => {
    if (!fetchedPitch) return;
    setPitch(fetchedPitch);
    setCreatedAt(fetchedPitch.createdAt);
    setUpdatedAt(fetchedPitch.updatedAt);
    // Initialize a sensible default form draft from the pitch
    // We only know the pitch title; description/context are unknown at MVP.
    setFormDraft((prev) => ({
      ...prev,
      productTitle: fetchedPitch.title,
      productDescription: prev.productDescription || "",
      targetCustomer: prev.targetCustomer ?? null,
      customerPainPoint: prev.customerPainPoint ?? null,
      additionalContext: prev.additionalContext ?? null,
    }));
    // Derive ownerName from org members mapping
    const name = membersNameMap[fetchedPitch.createdByUserId] ?? null;
    setOwnerName(name);
  }, [
    fetchedPitch,
    setPitch,
    setCreatedAt,
    setUpdatedAt,
    setFormDraft,
    membersNameMap,
    setOwnerName,
  ]);

  // -------------------------------------------
  // Local-only mutations (no server calls)
  // -------------------------------------------

  const enqueueToast = React.useCallback(
    (message: string, type: ToastMessage["type"]) => {
      const toast: ToastMessage = {
        id: generateUUID(),
        message,
        type,
        createdAt: new Date(),
      };
      setToasts((prev) => [toast, ...prev]);
    },
    [setToasts],
  );

  const dismissToast = React.useCallback(
    (id: string) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [setToasts],
  );

  // -------------------------------------------
  // Commands
  // -------------------------------------------

  const loadPitch = React.useCallback(
    async (pitchId: string) => {
      setErrorMessage(null);
      setIsLoading(true);
      setCurrentPitchId(pitchId);
      // React Query will fetch because the query is enabled by currentPitchId and teamId change
    },
    [setCurrentPitchId, setIsLoading, setErrorMessage],
  );

  // Progress helper: smoothly increment progress to a target cap during generation
  const startProgressLoop = React.useCallback(() => {
    setGenerationProgress(0);
    let progress = 0;
    const capWhilePending = 90; // Cap to 90% until mutation settles
    const interval = setInterval(() => {
      progress = Math.min(progress + Math.random() * 12 + 5, capWhilePending);
      setGenerationProgress(Math.floor(progress));
    }, 350);
    return () => clearInterval(interval);
  }, [setGenerationProgress]);

  // Helper: exponential backoff sleep
  const sleep = React.useCallback((ms: number) => new Promise((res) => setTimeout(res, ms)), []);

  const regenerateFromInputs = React.useCallback(async () => {
    if (!teamId) {
      const friendly = "No active team found. Please select a team and try again.";
      setErrorMessage(friendly);
      enqueueToast(friendly, "error");
      return;
    }

    setErrorMessage(null);
    setIsGenerating(true);
    setLastAttemptedFormDraft(formDraft);
    const stop = startProgressLoop();

    try {
      const maxAttempts = 3;
      const delays = [200, 400, 800]; // ms
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const persisted = await generateFromFormMutation.mutateAsync({
            form: {
              productTitle: formDraft.productTitle,
              productDescription: formDraft.productDescription,
              targetCustomer: formDraft.targetCustomer ?? null,
              customerPainPoint: formDraft.customerPainPoint ?? null,
              additionalContext: formDraft.additionalContext ?? null,
            },
            existingPitchId: pitch?.id ?? null,
            temperature: null,
            maxTokens: null,
            teamId: teamId,
          });

          // On success: update local state and cache, toast, and exit loop
          setPitch(persisted);
          setCreatedAt(persisted.createdAt);
          setUpdatedAt(persisted.updatedAt);
          setFormDraft((prev) => ({ ...prev, productTitle: persisted.title }));
          // Align current pitch id with persisted one (covers create/regenerate)
          setCurrentPitchId(persisted.id);

          await utils.main.pitches.byId.invalidate();
          await utils.main.pitches.list.invalidate();

          enqueueToast("Pitch generated", "success");
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
          const errMsg = (e as { message?: string })?.message ?? "";
          const shouldRetry = errMsg.includes("PITCH_SAVE_FAILED");
          if (attempt < maxAttempts && shouldRetry) {
            // Keep generating state true across retries
            await sleep(delays[attempt - 1]!);
            continue;
          }
          // Final failure or non-retryable error
          const friendly = mapFriendlyError(errMsg);
          setErrorMessage(friendly);
          enqueueToast(friendly, "error");
          break;
        }
      }
    } finally {
      // Complete progress and reset generating state
      stop();
      setGenerationProgress(100);
      setTimeout(() => setGenerationProgress(0), 300);
      setIsGenerating(false);
    }
  }, [
    teamId,
    setErrorMessage,
    enqueueToast,
    setIsGenerating,
    setLastAttemptedFormDraft,
    formDraft,
    startProgressLoop,
    generateFromFormMutation,
    pitch?.id,
    setPitch,
    setCreatedAt,
    setUpdatedAt,
    setFormDraft,
    setCurrentPitchId,
    utils.main.pitches.byId,
    utils.main.pitches.list,
    sleep,
  ]);

  const regenerate = React.useCallback(async () => {
    await regenerateFromInputs();
  }, [regenerateFromInputs]);

  const retryGenerate = React.useCallback(async () => {
    if (lastAttemptedFormDraft) {
      setFormDraft(lastAttemptedFormDraft);
    }
    await regenerateFromInputs();
  }, [lastAttemptedFormDraft, setFormDraft, regenerateFromInputs]);

  const savePitchContent = React.useCallback(
    async (pitchId: string, newBody: string) => {
      if (!teamId) {
        const friendly = "No active team found. Please select a team and try again.";
        setErrorMessage(friendly);
        enqueueToast(friendly, "error");
        return;
      }
      setErrorMessage(null);
      // Optimistic update
      const prev = pitch;
      const optimistic: Pitch | null = prev
        ? { ...prev, body: newBody, updatedAt: new Date() }
        : null;
      setPitch(optimistic);
      setUpdatedAt(optimistic?.updatedAt ?? null);
      try {
        await updateBodyMutation.mutateAsync({ id: pitchId, body: newBody, teamId });
      } catch (e) {
        // Revert optimistic update on error
        setPitch(prev ?? null);
        setUpdatedAt(prev?.updatedAt ?? null);
        const message =
          mapFriendlyError((e as { message?: string })?.message ?? "Failed to save pitch content");
        setErrorMessage(message);
        enqueueToast(message, "error");
      }
    },
    [pitch, teamId, setPitch, setUpdatedAt, updateBodyMutation, setErrorMessage, enqueueToast],
  );

  const renamePitch = React.useCallback(
    async (pitchId: string, newTitle: string) => {
      if (!teamId) {
        const friendly = "No active team found. Please select a team and try again.";
        setErrorMessage(friendly);
        enqueueToast(friendly, "error");
        return;
      }
      setErrorMessage(null);
      try {
        await updateTitleMutation.mutateAsync({ id: pitchId, title: newTitle, teamId });
      } catch (e) {
        const message = mapFriendlyError((e as { message?: string })?.message);
        setErrorMessage(message);
        enqueueToast(message, "error");
      }
    },
    [teamId, updateTitleMutation, setErrorMessage, enqueueToast],
  );

  const softDeletePitch = React.useCallback(
    async (pitchId: string) => {
      if (!teamId) {
        const friendly = "No active team found. Please select a team and try again.";
        setErrorMessage(friendly);
        enqueueToast(friendly, "error");
        return;
      }
      setErrorMessage(null);
      try {
        await softDeleteMutation.mutateAsync({ id: pitchId, teamId });
      } catch (e) {
        const message = mapFriendlyError((e as { message?: string })?.message);
        setErrorMessage(message);
        enqueueToast(message, "error");
      }
    },
    [teamId, softDeleteMutation, setErrorMessage, enqueueToast],
  );

  // If the consumer passed an initialPitchId, auto-load it once
  React.useEffect(() => {
    if (params.initialPitchId) {
      void loadPitch(params.initialPitchId);
    }
    // We intentionally want to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Final return complies with interface
  return {
    // State
    pitch,
    teamId,
    teamName,
    ownerName,
    lastEditorName,
    createdAt,
    updatedAt,
    isLoading,
    isGenerating,
    generationProgress,
    errorMessage,
    formDraft,
    toasts,

    // Mutations (local only)
    setFormDraft: React.useCallback(
      (next: PitchFormDraft) => {
        setFormDraft(next);
      },
      [setFormDraft],
    ),
    enqueueToast,
    dismissToast,

    // Commands
    loadPitch,
    regenerateFromInputs,
    regenerate,
    retryGenerate,
    savePitchContent,
    renamePitch,
    softDeletePitch,
  };
}